import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Info, Clock, Archive, Trophy, RefreshCw, FileText, Radio, Crosshair, Gift, Eye, CalendarDays, Star } from "lucide-react";
import api, { fileUrl, API } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { DataTableView } from "@/components/DataTableView";
import { PdfViewer } from "@/components/PdfViewer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { divisionTheme } from "@/lib/divisionTheme";
import { RichText, isRichEmpty } from "@/components/RichText";

const fmtReviewDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min > 1 ? "s" : ""} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? "s" : ""} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function DivisionDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [division, setDivision] = useState(null);
  const [error, setError] = useState("");
  const [activeDoc, setActiveDoc] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const docsRef = useRef(null);
  const tabsRef = useRef(null);
  const reviewRef = useRef(null);
  const weeklyRef = useRef(null);

  const scrollToRef = (ref) => {
    const el = ref.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  useEffect(() => {
    let active = true;
    api
      .get(`/divisions/${slug}`)
      .then((r) => { if (active) { setDivision(r.data); setError(""); } })
      .catch(() => { if (active) { setDivision(null); setError("Division not found."); } });
    return () => { active = false; };
  }, [slug]);

  const VIEW_KEYWORDS = { schedule: "schedule", standings: "standing", players: "player", teams: "team", rules: "rule" };

  useEffect(() => {
    if (!division) return;
    const tbls = division.tables || [];
    const view = searchParams.get("view");
    const kw = view ? VIEW_KEYWORDS[view] : null;
    const match = kw ? tbls.find((t) => (t.title || "").toLowerCase().includes(kw)) : null;
    setActiveTab(match ? match.id : (tbls[0]?.id || null));
    if (view) {
      setTimeout(() => {
        if (view === "review" && reviewRef.current) scrollToRef(reviewRef);
        else if (view === "rules" && !match) scrollToRef(docsRef);
        else if (match) scrollToRef(tabsRef);
      }, 350);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [division, searchParams]);

  const loading = !error && (!division || division.slug !== slug);
  const docs = useMemo(() => (division?.documents || []).filter((d) => d.url), [division]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-20 text-[#555555]">Loading…</div>
      </Layout>
    );
  }

  if (error || !division) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-20">
          <p className="text-[#555555] mb-4">{error || "Division not found."}</p>
          <Link to="/divisions" className="text-[#1E63C4] font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to divisions
          </Link>
        </div>
      </Layout>
    );
  }

  const tables = division.tables || [];
  const hasPlayerRatings = tables.some((t) => (t.fargo_report === "players_list") || (t.title || "").toLowerCase().includes("players list"));
  const stamp = division.archived ? `Final${division.season ? " · " + division.season : ""}` : null;
  const eyebrow = division.archived ? (division.season || "Previous season") : (division.night || "Division");
  const weekInfo = !division.archived ? (division.week || null) : null;
  const highlightsOn = division.weekly_highlights_enabled !== false;
  // Per-division visibility: hidden sections are treated as empty (content itself is untouched).
  const showWanted = division.show_wanted !== false;
  const showImp = division.show_improvement !== false;
  const wantedImage = showWanted ? division.wanted_image : "";
  const wantedPlayer = showWanted ? division.wanted_player : "";
  const impRows = showImp ? (division.improvement_giveaways_rows || []).filter((r) => (r.name || "").trim()) : [];
  const wantRows = showWanted ? (division.wanted_giveaways_rows || []).filter((r) => (r.name || "").trim()) : [];
  const hasHighlights = highlightsOn && !!(division.live_stream_image || wantedImage || division.live_stream_teams || wantedPlayer || impRows.length || wantRows.length);
  const theme = divisionTheme(division.color);
  const hasReview = !!(division.review && !isRichEmpty(division.review));

  return (
    <Layout>
      <Seo
        title={`${division.name} Standings & Stats | Quinte BCA`}
        description={`Standings, schedules, team results and player statistics for the ${division.name} division of the Quinte BCA Pool League in Trenton & the Quinte region.`}
        path={`/divisions/${division.slug || ""}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://quintebca.ca/" },
          { "@type": "ListItem", position: 2, name: "Divisions", item: "https://quintebca.ca/divisions" },
          { "@type": "ListItem", position: 3, name: division.name, item: `https://quintebca.ca/divisions/${division.slug || ""}` },
        ] }}
      />
      <PageHeader eyebrow={eyebrow} title={division.name} subtitle={division.description} accent={theme.hex} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" style={theme.vars}>
        <Link to="/divisions" className="inline-flex items-center gap-1 text-[var(--div-accent)] font-semibold mb-8 hover:gap-2 transition-all" data-testid="back-to-divisions">
          <ArrowLeft className="h-4 w-4" /> All divisions
        </Link>

        {(() => {
          const items = [];
          if (!division.archived && (weekInfo || hasHighlights)) items.push({ label: "Weekly", on: () => scrollToRef(weeklyRef) });
          if (hasReview) items.push({ label: "Review", on: () => scrollToRef(reviewRef) });
          if (docs.length) items.push({ label: "Documents", on: () => scrollToRef(docsRef) });
          tables.forEach((t) => items.push({ label: t.title, on: () => { setActiveTab(t.id); setTimeout(() => scrollToRef(tabsRef), 80); } }));
          if (items.length <= 1) return null;
          return (
            <nav className="flex flex-wrap gap-2 mb-10 -mt-3" aria-label="On this page" data-testid="division-quicknav">
              {items.map((it, i) => (
                <button
                  key={i}
                  onClick={it.on}
                  data-testid={`quicknav-${i}`}
                  className="rounded-full border border-[var(--div-border)] bg-[var(--div-tint)] text-[var(--div-accent)] px-4 py-1.5 text-sm font-semibold hover:bg-[var(--div-accent)] hover:text-white transition-colors"
                >
                  {it.label}
                </button>
              ))}
            </nav>
          );
        })()}

        {!division.archived && (weekInfo || hasHighlights) && (
          <div ref={weeklyRef} className="mb-10 space-y-6 scroll-mt-24" data-testid="division-highlights">
            {weekInfo && (
              <div className="flex items-center gap-2" data-testid="upcoming-week">
                <CalendarDays className="h-5 w-5 text-[var(--div-accent)]" />
                <h2 className="font-heading font-extrabold text-2xl text-[#111111]">Upcoming Week {weekInfo.current} of {weekInfo.total}</h2>
              </div>
            )}
            {highlightsOn && (division.live_stream_image || wantedImage) && (
              <div className="grid gap-6 sm:grid-cols-2">
                {division.live_stream_image && (
                  <a href={fileUrl(division.live_stream_image)} target="_blank" rel="noopener noreferrer" data-testid="division-live-stream-image" className="block rounded-xl overflow-hidden border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow">
                    <img src={fileUrl(division.live_stream_image)} alt="Upcoming live stream match" className="w-full h-auto object-contain bg-[#0B0B0C]" />
                  </a>
                )}
                {wantedImage && (
                  <a href={fileUrl(wantedImage)} target="_blank" rel="noopener noreferrer" data-testid="division-wanted-image" className="block rounded-xl overflow-hidden border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow">
                    <img src={fileUrl(wantedImage)} alt="This week's WANTED" className="w-full h-auto object-contain bg-[#0B0B0C]" />
                  </a>
                )}
              </div>
            )}
            {highlightsOn && (division.live_stream_teams || wantedPlayer) && (
              <div className="grid gap-4 sm:grid-cols-2">
                {division.live_stream_teams && (
                  <div className="flex items-center gap-3 bg-[#E11D2A]/5 border border-[#E11D2A]/30 rounded-lg p-4" data-testid="division-live-stream-teams">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E11D2A] text-white shrink-0"><Radio className="h-5 w-5" /></span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#E11D2A]">Upcoming Live Stream</p>
                      <p className="font-heading font-bold text-[#111111]">{division.live_stream_teams}</p>
                    </div>
                  </div>
                )}
                {wantedPlayer && (
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="division-wanted-player">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-white shrink-0"><Crosshair className="h-5 w-5" /></span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">This Week&apos;s WANTED</p>
                      <p className="font-heading font-bold text-[#111111]">{wantedPlayer}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {highlightsOn && (impRows.length > 0 || wantRows.length > 0) && (
              <div className="grid gap-6 sm:grid-cols-2">
                {impRows.length > 0 && (
                  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="division-improvement-giveaways">
                    <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Gift className="h-5 w-5 text-[#1E63C4]" /> Player Improvement Giveaways Qualifiers</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-[#777]">
                          <th className="py-1.5 pr-2 font-semibold">Name</th>
                          <th className="py-1.5 px-2 font-semibold text-right">Prev Avg</th>
                          <th className="py-1.5 pl-2 font-semibold text-right">Wkly Avg</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0F0F0]">
                        {impRows.map((r) => (
                          <tr key={r.id} className="text-[#333333]">
                            <td className="py-1.5 pr-2 font-medium">{r.name}</td>
                            <td className="py-1.5 px-2 text-right tabular-nums">{r.prev_avg || "—"}</td>
                            <td className="py-1.5 pl-2 text-right tabular-nums">{r.wkly_avg || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {wantRows.length > 0 && (
                  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="division-wanted-giveaways">
                    <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Gift className="h-5 w-5 text-amber-600" /> WANTED Giveaways Qualifiers</h3>
                    <div className="mb-4" data-testid="wanted-player-featured">
                      <p className="text-[11px] uppercase tracking-wide text-amber-600 font-semibold mb-1">WANTED Player</p>
                      <div className="inline-grid grid-cols-[auto_auto] gap-x-6 items-baseline">
                        <span className="font-bold text-[#111111]">{wantRows[0].name}</span>
                        <span className="font-semibold text-[#333333] tabular-nums">{wantRows[0].value || "—"}</span>
                      </div>
                    </div>
                    {wantRows.length > 1 && (
                      <div className="border-t border-[#F0F0F0] pt-3">
                        <div className="inline-grid grid-cols-[auto_auto] gap-x-6 gap-y-1.5 text-sm">
                          <span className="text-[11px] uppercase tracking-wide text-[#777] font-semibold">Name</span>
                          <span className="text-[11px] uppercase tracking-wide text-[#777] font-semibold">Score</span>
                          {wantRows.slice(1).map((r) => (
                            <Fragment key={r.id}>
                              <span className="font-medium text-[#333333]">{r.name}</span>
                              <span className="text-[#333333] tabular-nums">{r.value || "—"}</span>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {hasReview && (
          <div ref={reviewRef} className="scroll-mt-24 mb-10 rounded-xl border border-[var(--div-border)] bg-white shadow-sm overflow-hidden" data-testid="division-review-card">
            <div className="flex items-center gap-2 px-6 py-4 text-white" style={{ backgroundColor: "var(--div-accent)" }}>
              <Star className="h-5 w-5 shrink-0" />
              <h2 className="font-heading font-bold text-lg">Division Review</h2>
              {fmtReviewDate(division.review_date) && (
                <span className="ml-auto text-xs font-semibold bg-white/20 rounded-full px-3 py-1" data-testid="division-review-date">{fmtReviewDate(division.review_date)}</span>
              )}
            </div>
            <div className="p-6">
              <RichText html={division.review} testid="division-review-body" />
            </div>
          </div>
        )}

        {!division.archived && division.fargo_last_synced && (
          <p className="text-xs text-[#555555] -mt-5 mb-6 flex items-center gap-1.5" data-testid="last-updated" title={new Date(division.fargo_last_synced).toLocaleString()}>
            <RefreshCw className="h-3.5 w-3.5 text-[var(--div-accent)]" /> Ratings updated {timeAgo(division.fargo_last_synced)}
          </p>
        )}

        {division.archived && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 mb-6 flex gap-3" data-testid="archived-banner">
            <Archive className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-[#333333]">
              <p className="font-semibold">Completed season{division.season ? ` — ${division.season}` : ""}</p>
              <p className="leading-relaxed">These are the final standings and results for this division.</p>
            </div>
          </div>
        )}

        {division.archived && division.champion_team && (
          <div className="rounded-xl border border-[#E5E7EB] overflow-hidden mb-10 bg-white shadow-sm" data-testid="champion-block">
            {division.champion_image && (
              <div className="bg-[#0B0B0C]">
                <img src={fileUrl(division.champion_image)} alt={`${division.champion_team} champions`} className="w-full h-auto object-contain" />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 text-[#E11D2A] mb-1">
                <Trophy className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-wide">Season Champions</span>
              </div>
              <h3 className="font-heading font-extrabold text-2xl">{division.champion_team}</h3>
              {division.champion_players && <p className="text-sm text-[#333333] mt-2">{division.champion_players}</p>}
            </div>
          </div>
        )}

        {(division.info || division.night) && (
          <div className="bg-[var(--div-tint)] border border-[var(--div-border)] rounded-lg p-5 mb-10 flex gap-3">
            <Info className="h-5 w-5 text-[var(--div-accent)] shrink-0 mt-0.5" />
            <div className="text-sm text-[#333333]">
              {division.night && (
                <p className="font-semibold flex items-center gap-1.5 mb-1">
                  <Clock className="h-4 w-4 text-[var(--div-accent)]" /> {division.night}
                </p>
              )}
              {division.info && <RichText html={division.info} testid="division-info-body" />}
            </div>
          </div>
        )}

        {(division.documents || []).filter((d) => d.url).length > 0 && (
          <div className="mb-10" data-testid="division-documents" ref={docsRef}>
            <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[var(--div-accent)]" /> Documents
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {docs.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => setActiveDoc(doc)}
                  data-testid={`division-doc-${doc.id}`}
                  className="group flex w-full items-center gap-3 text-left bg-white rounded-lg border border-[#E5E7EB] p-4 hover:border-[var(--div-accent)] hover:shadow-sm transition-all"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--div-tint)] text-[var(--div-accent)] shrink-0">
                    <FileText className="h-5 w-5" />
                  </span>
                  <span className="flex-1 font-semibold text-[#111111] group-hover:text-[var(--div-accent)] transition-colors">{doc.title || "Document"}</span>
                  <Eye className="h-4 w-4 text-[var(--div-accent)]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {tables.length === 0 ? (
          <p className="text-[#555555]">No stats, standings or schedules have been posted for this division yet.</p>
        ) : (
          <div ref={tabsRef} className="scroll-mt-24">
          <Tabs value={activeTab || tables[0].id} onValueChange={setActiveTab} className="w-full" data-testid="division-tabs">
            <TabsList className="flex flex-wrap h-auto justify-start bg-[#F9FAF8] border border-[#E5E7EB] p-1 mb-8">
              {tables.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  data-testid={`tab-${t.id}`}
                  className="data-[state=active]:bg-[var(--div-accent)] data-[state=active]:text-white font-medium"
                >
                  {t.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {tables.map((t) => (
              <TabsContent key={t.id} value={t.id}>
                <DataTableView table={t} stamp={stamp} />
              </TabsContent>
            ))}
          </Tabs>
          </div>
        )}

        {hasPlayerRatings && (
          <p className="mt-8 text-sm text-[#777777]" data-testid="player-ratings-report-wrap">
            <a
              href={`${API}/reports/${division.slug}/player-ratings`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="player-ratings-report-link"
              className="text-[var(--div-accent)] font-semibold hover:underline"
            >
              View printable player ratings report
            </a>
          </p>
        )}
      </div>

      {activeDoc && <PdfViewer doc={activeDoc} onClose={() => setActiveDoc(null)} />}
    </Layout>
  );
}
